import React, { useState } from "react";
import Navbar from "../components/layout/Navbar";
import Reveal from "../components/common/Reveal";
import Footer from "../components/layout/Footer";
import { SONGS } from "../data/songs";


const ResourcesPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>("All");
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>("All");

  // Get unique categories
  const categories = ["All", ...Array.from(new Set(SONGS.map((song) => song.category)))];
  const difficulties = ["All", "Beginner", "Intermediate", "Advanced"];

  // Filter songs
  const filteredSongs = SONGS.filter((song) => {
    const categoryMatch = selectedCategory === "All" || song.category === selectedCategory;
    const difficultyMatch = selectedDifficulty === "All" || song.difficulty === selectedDifficulty;
    return categoryMatch && difficultyMatch;
  });

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case "Beginner":
        return "bg-green-100 text-green-700 border-green-200";
      case "Intermediate":
        return "bg-yellow-100 text-yellow-700 border-yellow-200";
      case "Advanced":
        return "bg-red-100 text-red-700 border-red-200";
      default:
        return "bg-gray-100 text-gray-700 border-gray-200";
    }
  };

  return (
    <div className="min-h-screen bg-primary">
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary via-accentGold/5 to-accentNavy/10 py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-accentNavy/20 via-transparent to-accentGold/15" />
        <div className="absolute -right-32 top-1/2 h-96 w-96 rounded-full bg-accentGold/30 blur-3xl" />
        <div className="absolute -left-32 bottom-1/4 h-96 w-96 rounded-full bg-accentNavy/20 blur-3xl" />

        <div className="relative z-10 mx-auto max-w-6xl px-6 lg:px-10">
          <Reveal className="text-center space-y-6">
            <p className="text-xs uppercase tracking-[0.35em] text-accentNavy/45 font-semibold">
              Practice Resources
            </p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl text-accentNavy">
              Ukulele Tabs & Songs
            </h1>
            <p className="mx-auto max-w-2xl text-lg text-accentNavy/70 leading-relaxed">
              Learn your favorite songs with our curated collection of ukulele tabs
            </p>
          </Reveal>
        </div>
      </section>

      {/* Filters */}
      <section className="relative bg-gradient-to-b from-primary via-primary/95 to-primary/90 py-12">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <div className="flex flex-col items-center gap-8 md:flex-row md:gap-4 justify-center">
              {/* Category Filter */}
              <div className="flex flex-col items-center gap-3 md:flex-row md:gap-2">
                <span className="text-sm font-semibold text-accentNavy/70">Category:</span>
                <div className="flex flex-wrap justify-center gap-2 rounded-2xl border border-accentNavy/10 bg-white/50 p-1.5 backdrop-blur">
                  {categories.map((category) => (
                    <button
                      key={category}
                      type="button"
                      onClick={() => setSelectedCategory(category)}
                      className={[
                        "rounded-xl px-4 py-2 text-sm font-semibold uppercase tracking-[0.1em] transition-all duration-300",
                        selectedCategory === category
                          ? "bg-white text-accentNavy shadow-[0_4px_12px_-4px_rgba(9,32,63,0.25)]"
                          : "text-accentNavy/60 hover:text-accentNavy/80",
                      ].join(" ")}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>

              {/* Difficulty Filter */}
              <div className="flex flex-col items-center gap-3 md:flex-row md:gap-2">
                <span className="text-sm font-semibold text-accentNavy/70">Difficulty:</span>
                <div className="flex flex-wrap justify-center gap-2 rounded-2xl border border-accentNavy/10 bg-white/50 p-1.5 backdrop-blur">
                  {difficulties.map((difficulty) => (
                    <button
                      key={difficulty}
                      type="button"
                      onClick={() => setSelectedDifficulty(difficulty)}
                      className={[
                        "rounded-xl px-4 py-2 text-sm font-semibold uppercase tracking-[0.1em] transition-all duration-300",
                        selectedDifficulty === difficulty
                          ? "bg-white text-accentNavy shadow-[0_4px_12px_-4px_rgba(9,32,63,0.25)]"
                          : "text-accentNavy/60 hover:text-accentNavy/80",
                      ].join(" ")}
                    >
                      {difficulty}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Songs Grid */}
      <section className="relative bg-gradient-to-b from-primary via-primary/95 to-primary/90 py-20 pb-32">
        <div className="absolute inset-x-0 top-0 h-20 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.45),_transparent_60%)]" />
        <div className="absolute inset-x-0 bottom-0 h-32 bg-[radial-gradient(circle_at_bottom,_rgba(255,255,255,0.25),_transparent_65%)]" />

        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          {filteredSongs.length === 0 ? (
            <Reveal>
              <div className="text-center py-20">
                <p className="text-lg text-accentNavy/70">No songs found matching your filters.</p>
              </div>
            </Reveal>
          ) : (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {filteredSongs.map((song, index) => (
                <Reveal key={song.id} delay={index * 80}>
                  <article className="group relative flex h-full flex-col rounded-3xl border border-white/50 bg-white/90 overflow-hidden shadow-[0_20px_50px_-30px_rgba(9,32,63,0.3)] transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_25px_60px_-25px_rgba(9,32,63,0.4)]">
                    <div className="flex flex-1 flex-col p-6">
                      {/* Header */}
                      <div className="mb-4 flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <h2 className="mb-1 text-xl font-semibold text-accentNavy leading-tight group-hover:text-accentGold transition-colors duration-300">
                            {song.title}
                          </h2>
                          <p className="text-sm text-accentNavy/60">{song.artist}</p>
                        </div>
                        <span
                          className={[
                            "rounded-full border px-3 py-1 text-xs font-semibold whitespace-nowrap",
                            getDifficultyColor(song.difficulty),
                          ].join(" ")}
                        >
                          {song.difficulty}
                        </span>
                      </div>

                      {/* Footer */}
                      <div className="mt-auto flex items-center justify-between border-t border-accentNavy/10 pt-4">
                        <span className="text-xs font-semibold uppercase tracking-[0.15em] text-accentNavy/50">
                          {song.category}
                        </span>
                        <a
                          href={`#song-detail?id=${song.id}`}
                          className="rounded-full border border-accentNavy/20 bg-white/80 px-4 py-2 text-xs font-semibold uppercase tracking-[0.15em] text-accentNavy transition-all duration-300 hover:border-accentGold hover:bg-accentGold hover:text-white inline-block"
                        >
                          View Full Tab
                        </a>
                      </div>
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ResourcesPage;

